import { Button, Divider, MenuItem, Paper, Select, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSocket } from "../../context/useSocket";
import SimpleLinearRegression from 'ml-regression-simple-linear';
import * as turf from '@turf/turf';

import { RTFOSeismicityChart } from "./RTFOSeismicityChart";
import { RTFOSeismicityDataTable } from "./RTFOSeismicityDataTable";
import { useMemo, useState } from "react";
import { RTFOSeismicityPanel } from "./RTFOSeismicityPanel";
import { RTFOSeismicityGutenbergChart } from "./RTFOSeismicityGutenbergChart";
import { RTFOSeismicityHistogramChart } from "./RTFOSeismicityHistogramChart";
import { RTFOSeismicityMaxPredictedMagnitudeChart } from "./RTFOSeismicityMaxPredictedMagnitudeChart";
import { RTFOSeismicityFracEnergy } from "./RTFOSeismicityFracEnergy";
import { RTFOSeismicityFracEnergyLostChart } from "./RTFOSeismicityFracEnergyLostChart";
import { RTFOSeismicityFracEnergyDataTable } from "./RTFOSeismicityFracEnergyDataTable";
import { useSelector } from "react-redux";
import { selectUser } from "../../reducers/userSlice";

export const RTFOSeismicity = ({ darkMode, currentRTFO, gridView=true }) => {

  const user = useSelector(selectUser);
  const theme = useTheme();
  const { socket } = useSocket();

  const isHighPrecisionSeismicityData = currentRTFO?.seismicData?.highPrecision || false;

  const [seismicityDataType, setSeismicityDataType] = useState(isHighPrecisionSeismicityData ? 'highPrecision' : 'realtime');
  const [selectedFeature, setSelectedFeature] = useState('None');

  const seismicityData = useMemo(() => {
    if (!currentRTFO?.seismicData || !currentRTFO?.seismicData?.realtime) return [];

    let allEvents = [...(currentRTFO?.seismicData?.reprocessed || [])];
    for (const event of currentRTFO?.seismicData?.realtime || []) {
      if (!allEvents?.find(e => e.id === event.id && e.type === event.type)) {
        allEvents.push(event);
      };
    };

    let sortedEvents = [
      ...(seismicityDataType === 'highPrecision'
        ? currentRTFO?.seismicData?.realtime?.filter(e => e.type === 'd')
        : seismicityDataType === 'realtime'
          ? allEvents
          : seismicityDataType === 'reprocessed'
            ? allEvents.filter(e => e.type !== 'a' && e.type !== 'd')
            : [])
    ];

    sortedEvents = sortedEvents?.filter(e => e && Object.keys(e)?.length > 0) || [];
    sortedEvents?.sort((a, b) => b.time - a.time);

    if (currentRTFO?.mapFeatures?.length > 0 && selectedFeature?.length > 0 && selectedFeature !== 'None') {
      try {
        const mapFeature = currentRTFO?.mapFeatures?.find(f => f.name === selectedFeature);
        
        if (!mapFeature) return sortedEvents;

        let polygonPoints = mapFeature?.layer?.latlngs?.flat(Infinity);
    
        polygonPoints = polygonPoints?.map(p => [p.lng, p.lat]);
  
        // Ensure the polygon is closed by adding the first coordinate at the end if it's not already
        if (
          polygonPoints[0][0] !== polygonPoints[polygonPoints.length - 1][0] ||
          polygonPoints[0][1] !== polygonPoints[polygonPoints.length - 1][1]
        ) {
          polygonPoints.push(polygonPoints[0]);
        };
  
        const polygon = turf.polygon([polygonPoints]);
  
        sortedEvents = sortedEvents.filter(e => {
          const point = turf.point([e.longitude, e.latitude]);
          return turf.booleanPointInPolygon(point, polygon);
        });

      } catch(error) {
        console.log(error)
      };
    };

    return sortedEvents;
  }, [currentRTFO?.mapFeatures, currentRTFO?.seismicData, seismicityDataType, selectedFeature]);


  const gutenbergRichterResult = useMemo(() => {
    return calculateGutenbergRichter(seismicityData);
  }, [seismicityData]);

  const [maxPredictedMagnitudes, setMaxPredictedMagnitudes] = useState(currentRTFO?.seismicData?.maxPredictedMagnitudes || []);

  try {
    return (
      <Paper elevation={0} sx={{boxShadow: 1}} style={{margin: '10px 20px', display: 'flex', position: 'relative', flexDirection: 'column', justifyContent: 'center', backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')])}}>
        
        <Typography variant="h6" style={{margin: '20px 20px 0px 20px'}}>
          {seismicityDataType === 'realtime' ? 'All' : seismicityDataType === 'reprocessed' ? 'Confirmed' : 'High Precision'} Seismicity Summary
        </Typography>

        <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
          <div style={{width: '100%', height: 900, padding: '20px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative'}}>
            <div style={{marginTop: 0, position: 'absolute', top: -20, right: 20, display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', height: 28, marginRight: 20}}>
                <Typography variant="caption" style={{marginRight: 10}}>Map feature:</Typography>
                <Select
                  value={selectedFeature}
                  onChange={handleFeatureChange}
                  size="small"
                  variant="outlined"
                  style={{width: 200, height: 28, zIndex: 69, fontSize: '14px'}}
                >
                  {['None', ...currentRTFO?.mapFeatures || []].map((feature, i) => {
                    return (
                      <MenuItem key={`${feature?.name || 'None'}-${i}`} value={feature.name || 'None'}>{feature?.name || 'None'}</MenuItem>
                    )
                  })}
                </Select>
              </div>
              
              
              <Button
                size="small"
                variant="contained"
                style={{height: 28, marginRight: 20}}
                onClick={() => {
                  if (socket.readyState !== 1) return;

                  socket?.send(JSON.stringify({ // request seismic report
                    messageType: 'generateSeismicityReport',
                    padName: currentRTFO?.name,
                    client: currentRTFO?.client
                  }));
                }}
                disabled={user?.value?.email !== 'aborisov@ssdynamics.com'}
              >
                Generate Seismicity Report
              </Button>

              <ToggleButtonGroup
                value={seismicityDataType}
                size="small"
                style={{height: 24}}
                exclusive
                onChange={handleSeismicDataChange}
              >
                <ToggleButton value="realtime" size="small" title="Show all induced seismicity events" color={(darkMode ? "" : "primary")}>
                  All
                </ToggleButton>
                <ToggleButton value="reprocessed" size="small" title="Show confirmed induced seismicity events" color={(darkMode ? "" : "primary")}>
                  Confirmed
                </ToggleButton>
                <ToggleButton value="highPrecision" size="small" title="Show high precision induced seismicity events" color={(darkMode ? "" : "primary")}>
                  High Precision
                </ToggleButton>
              </ToggleButtonGroup>
         
            </div>

            {currentRTFO ? <RTFOSeismicityChart seismicityData={seismicityData} />
            :
            <div>
              <Typography variant="body2">No data selected</Typography>
            </div>
            }
          </div>  
        </div>
  
        <Divider style={{margin: 20}} />

        <RTFOSeismicityPanel selectedFeature={selectedFeature} currentRTFO={currentRTFO} darkMode={darkMode}/>

        {currentRTFO?.seismicData?.realtime?.length > 0 && 
        <div>
          <Divider style={{margin: 20}} />

          <Typography variant="h6" style={{marginLeft: 20}}>
            Gutenberg-Richter Analysis
          </Typography>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <div style={{ height: 540, padding: 0, width: '50%' }}>
              <RTFOSeismicityGutenbergChart currentRTFOName={currentRTFO?.name} gutenbergRichterResult={gutenbergRichterResult} />
            </div>
            <div style={{ height: 540, padding: 0, width: '50%' }}>
              <RTFOSeismicityHistogramChart currentRTFOName={currentRTFO?.name} gutenbergRichterResult={gutenbergRichterResult} />
            </div>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', marginTop: 20}}>
            <div style={{ height: 540,  width: '100%' }}>
              <RTFOSeismicityMaxPredictedMagnitudeChart currentRTFOName={currentRTFO?.name} maxPredictedMagnitudes={maxPredictedMagnitudes} gridView={gridView} />
            </div>

            

          </div>
        </div>}

        <Divider style={{margin: 20}} />

        <Typography variant="h6" style={{marginLeft: 20}}>
          Fracture Energy
        </Typography>

        <RTFOSeismicityFracEnergy fracEnergy={currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.fracEnergy || {}} darkMode={darkMode} />
        
        <div style={{ height: 540,  width: '100%' }}>
          <RTFOSeismicityFracEnergyLostChart currentRTFO={currentRTFO} gridView={gridView} />
        </div>


        <Divider style={{margin: 20}} />


        {currentRTFO?.seismicData?.realtime?.length > 0 &&
          <div>
            <Typography variant="h6" style={{marginLeft: 20}}>
            {seismicityDataType === 'realtime' ? 'All' : seismicityDataType === 'reprocessed' ? 'Confirmed' : 'High Precision'} Seismicity Data
            </Typography>
            <div style={{ height: 700, padding: 20 }}>
              <RTFOSeismicityDataTable currentRTFO={currentRTFO} seismicityData={seismicityData} darkMode={darkMode} />
            </div>

          <Divider style={{margin: 20}} />
          </div>
        }

        <Typography variant="h6" style={{marginLeft: 20}}>
          Frac Energy Data
        </Typography>
        <div style={{ height: 700, padding: 20 }}>
          <RTFOSeismicityFracEnergyDataTable currentRTFO={currentRTFO} darkMode={darkMode} />
        </div>
      </Paper>
    );
  } catch (error) {
    return <></>;
  };

  function handleSeismicDataChange(e, newVal) {
    try {
      if (!newVal) return;
      setSeismicityDataType(newVal);
    } catch (error) {
      console.log(error);
    };
  };

  function handleFeatureChange(e) {
    try {
      if (!e?.target?.value) return;

      const newFeature = e.target.value;

      let newMaxMagnitudes = [];

      let seismicityEvents = currentRTFO?.seismicData?.realtime;
      

      if (newFeature === 'None') {
        newMaxMagnitudes = calculateGutenbergRichterHistory(seismicityEvents);
      } else {
        try {
          const mapFeature = currentRTFO?.mapFeatures?.find(f => f.name === newFeature);
          if (mapFeature) {
            let polygonPoints = mapFeature?.layer?.latlngs?.flat(Infinity);
        
            polygonPoints = polygonPoints?.map(p => [p.lng, p.lat]);
      
            // Ensure the polygon is closed by adding the first coordinate at the end if it's not already
            if (
              polygonPoints[0][0] !== polygonPoints[polygonPoints.length - 1][0] ||
              polygonPoints[0][1] !== polygonPoints[polygonPoints.length - 1][1]
            ) {
              polygonPoints.push(polygonPoints[0]);
            };
      
            const polygon = turf.polygon([polygonPoints]);
      
            seismicityEvents = seismicityEvents.filter(e => {
              const point = turf.point([e.longitude, e.latitude]);
              return turf.booleanPointInPolygon(point, polygon);
            });

            newMaxMagnitudes = calculateGutenbergRichterHistory(seismicityEvents);
          };
        } catch(error) {
          console.log(error);
        };
      };

      setSelectedFeature(newFeature);


      newMaxMagnitudes?.length > 0 && setMaxPredictedMagnitudes(newMaxMagnitudes);

    } catch (error) {
      console.log(error);
    };
  };  
};

// calculate slope and intercept
function calculateGutenbergRichter(seismicityData) {
  try {

    if (!seismicityData?.length > 0) return {};

    // get an array of local magnitude
    const allMagnitudes = seismicityData?.map(e => +e.localMagnitude || 0);
  
    // get min and max
    const minMagnitude = Math.min(...allMagnitudes);
    const maxMagnitude = Math.max(...allMagnitudes);

    // construct bins
    const bins = [];
    for (let binMin = minMagnitude; binMin <= maxMagnitude; binMin += 0.1) {
      const binMax = binMin + 0.1;
      const binAverage = ((binMin + binMax) / 2);
      const eventsInBin = allMagnitudes.filter(e => e >= binMin && e < binMax)?.length;
      bins.push({
        binMin,
        binMax,
        binAverage,
        eventsInBin
      });
    };
  
    // calculate bin values
    for (let i = 0; i < bins.length; i++) {
      const bin = bins[i];
      const cumulativeEvents = bins.slice(i, )?.map(b => b.eventsInBin)?.reduce((tot, curr) => tot + curr, 0);
      const log10CumulativeEvents = Math.log10(cumulativeEvents);
      bin.cumulativeEvents = cumulativeEvents;
      bin.log10CumulativeEvents = log10CumulativeEvents;
    };
  
    // calculate Mc
    const maxEventsInBin = Math.max(...bins.map(b => b.eventsInBin));
    const Mc = bins.find(b => b.eventsInBin === maxEventsInBin)?.binAverage;
  
    // calculate linear regression
    const filteredBins = bins.filter(b => b.binAverage >= Mc);
    // const filteredBins = bins.filter(b => b.binAverage >= Mc && b.log10CumulativeEvents > 0);

    const X = [];
    const Y = [];

    filteredBins.forEach(b => {
      X.push(b.binAverage);
      Y.push(b.log10CumulativeEvents);
    });

    const regression = new SimpleLinearRegression(X, Y);
    const slope = (regression.slope);
    const intercept = (regression.intercept);
    const maxEvent = +(((intercept - Math.log10(1.0)) / Math.abs(slope))?.toFixed(2));
  
    return {bins, Mc, slope, intercept, maxEvent};

  } catch (error) {
    console.log(error);
    return {};
  };
};

function calculateGutenbergRichterHistory(events) {
  try {

    if (!events?.length > 0) return {};
    // determine start and end timestamps of the events
    const eventsStartTimestamp = events?.[events?.length - 1]?.time;
    const eventsEndTimestamp = events?.[0]?.time;

    const maxPredictedMagnitudes = [];

    const timeStep = 12 * 60 * 60 * 1000; // 12 hrs step
    const numTimeSteps = Math.ceil((eventsEndTimestamp - eventsStartTimestamp) / timeStep);

    let currentTimestamp = new Date(eventsStartTimestamp);
    currentTimestamp?.setHours(18);
    currentTimestamp?.setMinutes(0);
    currentTimestamp?.setSeconds(0);
    currentTimestamp = currentTimestamp?.getTime();

    for (let step = 1; step <= numTimeSteps; step++) {
      const filteredEvents = []
      for (const event of events) {
        if (event.time <= currentTimestamp) filteredEvents.push(event);
      };
      // events.filter(e => e.time <= currentTimestamp);
      const maxPredictedMagnitude = calculateGutenbergRichter(filteredEvents)?.maxEvent || 0;
      maxPredictedMagnitudes.push({
        timestamp: currentTimestamp,
        maxPredictedMagnitude
      });
      currentTimestamp += timeStep;
    };

    return maxPredictedMagnitudes;
  } catch (error) {
    console.log(error);
    return {};
  };
};
